import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Search, Users, Shield, Car, Store, UserX, Loader2, Phone, Mail, IdCard, Calendar, Clock, X, ChevronRight } from 'lucide-react';
interface UserSummary {
  user_id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  national_id: string | null;
  profile_is_active: boolean;
  is_platform_admin: boolean;
  platform_role: string | null;
  is_driver: boolean;
  driver_id: number | null;
  has_restaurant_access: boolean;
  restaurant_access_count: number;
  restaurant_roles: string[] | null;
  auth_created_at: string;
  last_sign_in_at: string | null;
}
export function UsersAdminPage() {
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Filters
  const [filterAdmin, setFilterAdmin] = useState(false);
  const [filterDriver, setFilterDriver] = useState(false);
  const [filterRestaurant, setFilterRestaurant] = useState(false);
  const [filterInactive, setFilterInactive] = useState(false);
  // Drawer state
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null);
  useEffect(() => {
    fetchUsers();
  }, []);
  const fetchUsers = async () => {
    setLoading(true);
    const {
      data,
      error
    } = await supabase.from('v_user_access_summary').select('*').order('auth_created_at', {
      ascending: false
    });
    if (!error && data) {
      setUsers(data);
    } else {
      console.error('Error fetching users:', error);
    }
    setLoading(false);
  };
  // Apply filters and search
  const filteredUsers = users.filter(u => {
    // Search
    const s = search.toLowerCase();
    const matchesSearch = !s || u.full_name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s) || u.phone?.includes(s) || u.national_id?.includes(s);
    if (!matchesSearch) return false;
    // Toggles (if any toggle is active, user must match ALL active toggles)
    if (filterAdmin && !u.is_platform_admin) return false;
    if (filterDriver && !u.is_driver) return false;
    if (filterRestaurant && !u.has_restaurant_access) return false;
    if (filterInactive && u.profile_is_active) return false;
    return true;
  });
  const formatDate = (isoString: string | null) => {
    if (!isoString) return 'Nunca';
    return new Date(isoString).toLocaleDateString('es-CR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans flex" data-id="element-6344" data-mp="1m4dz5hf5botr">
      <div className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-id="element-6345" data-mp="1h4rkd3jo60iu">
        {/* Header */}
        <div className="flex items-center gap-4" data-id="element-6346" data-mp="2xj19w0zndvb9">
          <div className="w-12 h-12 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center" data-id="element-6347" data-mp="2xhq45vmn6dhi">
            <Users className="w-6 h-6 text-[#006847]" data-id="element-6348" data-mp="2sjoolkofcwas" />
          </div>
          <div data-id="element-6349" data-mp="30zvy33h0ii5v">
            <h1 className="text-2xl font-bold" data-id="element-6350" data-mp="30zdwrsfhtdr3">Usuarios del Sistema</h1>
            <p className="text-white/40 text-sm" data-id="element-6351" data-mp="352skz6dl50fm">
              {filteredUsers.length}{' '}
              {filteredUsers.length === 1 ? 'usuario' : 'usuarios'} encontrados
            </p>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="space-y-4" data-id="element-6352" data-mp="2w3ym19cs7xyw">
          <div className="relative" data-id="element-6353" data-mp="3jqwzdy5lupsl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20" data-id="element-6354" data-mp="3jryk6ucfaqni" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre, email, teléfono o cédula..." className="w-full bg-[#141414] border border-white/10 rounded-xl pl-12 pr-4 py-4 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847]" data-id="element-6355" data-mp="3oq04tjphyy13" />
          </div>

          <div className="flex flex-wrap gap-2" data-id="element-6356" data-mp="2iuy8wdx6epzn">
            <button onClick={() => setFilterAdmin(!filterAdmin)} className={`px-4 py-2 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${filterAdmin ? 'bg-[#006847] text-white' : 'bg-[#141414] border border-white/10 text-white/50 hover:bg-white/5'}`} data-id="element-6357" data-mp="2iwj6kcesep1q">
              <Shield className="w-3.5 h-3.5" data-id="element-6358" data-mp="24xruswnedfgr" /> Admin NOIZA
            </button>
            <button onClick={() => setFilterDriver(!filterDriver)} className={`px-4 py-2 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${filterDriver ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-[#141414] border border-white/10 text-white/50 hover:bg-white/5'}`} data-id="element-6359" data-mp="32ie42epkbku7">
              <Car className="w-3.5 h-3.5" data-id="element-6360" data-mp="3la6uc1d8yry8" /> Conductores
            </button>
            <button onClick={() => setFilterRestaurant(!filterRestaurant)} className={`px-4 py-2 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${filterRestaurant ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-[#141414] border border-white/10 text-white/50 hover:bg-white/5'}`} data-id="element-6361" data-mp="3giyp7hiy9sqv">
              <Store className="w-3.5 h-3.5" data-id="element-6362" data-mp="1oznlcs0li01" /> Restaurante
            </button>
            <button onClick={() => setFilterInactive(!filterInactive)} className={`px-4 py-2 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${filterInactive ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-[#141414] border border-white/10 text-white/50 hover:bg-white/5'}`} data-id="element-6363" data-mp="6nko7db6i3p0">
              <UserX className="w-3.5 h-3.5" data-id="element-6364" data-mp="pd0qa52ii47j" /> Inactivos
            </button>
          </div>
        </div>

        {/* List */}
        {loading ? <div className="flex justify-center py-20" data-id="element-6365" data-mp="udezvrjrhm7o">
            <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-6366" data-mp="ycntmwesxk38" />
          </div> : filteredUsers.length === 0 ? <div className="text-center py-20 bg-[#141414] border border-white/10 rounded-2xl" data-id="element-6367" data-mp="2vel9zr0dxzsn">
            <Users className="w-12 h-12 text-white/10 mx-auto mb-4" data-id="element-6368" data-mp="2qg069hlxcxzg" />
            <p className="text-white/40 text-lg font-medium" data-id="element-6369" data-mp="346n83imzf3zr">
              No se encontraron usuarios
            </p>
          </div> : <div className="grid gap-3" data-id="element-6370" data-mp="2ze3s6f0tnwrs">
            {filteredUsers.map(user => <div key={user.user_id} onClick={() => setSelectedUser(user)} className="bg-[#141414] border border-white/10 rounded-2xl p-4 flex items-center gap-4 cursor-pointer hover:bg-white/5 transition-colors group" data-id="element-6371" data-mp="3e3026hmfvjv1">
                {/* Avatar */}
                <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center shrink-0 text-xl font-black text-white/30" data-id="element-6372" data-mp="3j45qm392gkyr">
                  {user.full_name?.charAt(0)?.toUpperCase() || '?'}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0" data-id="element-6373" data-mp="1emherevvkm7q">
                  <div className="flex items-center gap-2 mb-1" data-id="element-6374" data-mp="1enshrchhxpxs">
                    <h3 className="font-bold text-white truncate" data-id="element-6375" data-mp="1ilqauqee0w6g">
                      {user.full_name || 'Sin nombre'}
                    </h3>
                    {!user.profile_is_active && <span className="bg-red-500/10 text-red-400 text-[10px] uppercase font-black px-2 py-0.5 rounded-md shrink-0" data-id="element-6376" data-mp="19tertpllsb0i">
                        Inactivo
                      </span>}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-white/50 truncate" data-id="element-6377" data-mp="zu5v4ozcsiag">
                    <span className="truncate" data-id="element-6378" data-mp="lz3pm2yf4uy6">{user.email}</span>
                    {user.phone && <>
                        <span className="w-1 h-1 bg-white/20 rounded-full shrink-0" data-id="element-6379" data-mp="2ga8qx0m18vac" />
                        <span className="shrink-0" data-id="element-6380" data-mp="2l9lbfepflq9r">{user.phone}</span>
                      </>}
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mt-2" data-id="element-6381" data-mp="3tweofijtt355">
                    {user.is_platform_admin && <span className="inline-flex items-center gap-1 bg-[#006847]/20 text-[#00a870] text-xs font-bold px-2 py-0.5 rounded-md" data-id="element-6382" data-mp="3oxjzrzz6gkd4">
                        <Shield className="w-3 h-3" data-id="element-6383" data-mp="3g1uio34ric5j" /> Admin NOIZA
                      </span>}
                    {user.is_driver && <span className="inline-flex items-center gap-1 bg-blue-500/10 text-blue-400 text-xs font-bold px-2 py-0.5 rounded-md" data-id="element-6384" data-mp="364ptdufme0ki">
                        <Car className="w-3 h-3" data-id="element-6385" data-mp="316ocng5o2dc7" /> Conductor
                      </span>}
                    {user.has_restaurant_access && <span className="inline-flex items-center gap-1 bg-purple-500/10 text-purple-400 text-xs font-bold px-2 py-0.5 rounded-md" data-id="element-6386" data-mp="ze018jivcjlc">
                        <Store className="w-3 h-3" data-id="element-6387" data-mp="vd6fnwswfgvm" /> Restaurante (
                        {user.restaurant_access_count})
                      </span>}
                  </div>
                </div>

                <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-white/50 transition-colors shrink-0" data-id="element-6388" data-mp="2i79yt1a6mta7" />
              </div>)}
          </div>}
      </div>

      {/* Detail Drawer */}
      {selectedUser && <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => setSelectedUser(null)} data-id="element-6389" data-mp="1ud09b980cd1u" />

          {/* Drawer */}
          <div className="fixed inset-y-0 right-0 w-full max-w-md bg-[#141414] border-l border-white/10 z-50 flex flex-col shadow-2xl animate-in slide-in-from-right duration-200" data-id="element-6390" data-mp="23b2fs4aifxwi">
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10" data-id="element-6391" data-mp="3eqhcd6yt4nr3">
              <h2 className="text-lg font-bold" data-id="element-6392" data-mp="3jp2dd8lvvbgl">Detalles del Usuario</h2>
              <button onClick={() => setSelectedUser(null)} className="w-8 h-8 bg-white/5 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors" data-id="element-6393" data-mp="2dcf40faizchp">
                <X className="w-4 h-4" data-id="element-6394" data-mp="2n2i1aroojclu" />
              </button>
            </div>

            {/* Drawer Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-6395" data-mp="2s2m9zvjcj31i">
              {/* Profile Header */}
              <div className="flex items-center gap-4" data-id="element-6396" data-mp="1zc4at5lqtltd">
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center text-2xl font-black text-white/30" data-id="element-6397" data-mp="1zcnt4nfb2c7x">
                  {selectedUser.full_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div data-id="element-6398" data-mp="28ahckxrz1bdo">
                  <h3 className="text-xl font-bold text-white" data-id="element-6399" data-mp="3fqnn7hrgz7n9">
                    {selectedUser.full_name || 'Sin nombre'}
                  </h3>
                  <p className="text-white/50 text-sm" data-id="element-6400" data-mp="3jqecfh1clfpo">{selectedUser.email}</p>
                </div>
              </div>

              {/* Roles Summary */}
              <div className="space-y-3" data-id="element-6401" data-mp="142ikxd3xei24">
                <h4 className="text-xs font-bold text-white/30 uppercase tracking-widest" data-id="element-6402" data-mp="143k4k5z0ccvg">
                  Roles en la plataforma
                </h4>
                <div className="grid gap-2" data-id="element-6403" data-mp="udqvpcq92buw">
                  {selectedUser.is_platform_admin && <div className="bg-[#006847]/10 border border-[#006847]/20 rounded-xl p-3 flex items-center gap-3" data-id="element-6404" data-mp="pens7wpx4dp1">
                      <div className="w-8 h-8 bg-[#006847]/20 rounded-lg flex items-center justify-center" data-id="element-6405" data-mp="ke03xit2qayi">
                        <Shield className="w-4 h-4 text-[#00a870]" data-id="element-6406" data-mp="ge95qu17wsjy" />
                      </div>
                      <div data-id="element-6407" data-mp="bfobrcck753y">
                        <p className="font-bold text-[#00a870] text-sm" data-id="element-6408" data-mp="bgfzy23tu0k7">
                          Admin NOIZA
                        </p>
                        <p className="text-xs text-[#00a870]/70" data-id="element-6409" data-mp="25ruqum8kq61j">
                          {selectedUser.platform_role || 'Administrador'}
                        </p>
                      </div>
                    </div>}

                  {selectedUser.is_driver && <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-3 flex items-center gap-3" data-id="element-6410" data-mp="2fwkwb236rsai">
                      <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center" data-id="element-6411" data-mp="2kumezlwp9plg">
                        <Car className="w-4 h-4 text-blue-400" data-id="element-6412" data-mp="38m1jnlby8izv" />
                      </div>
                      <div className="flex-1" data-id="element-6413" data-mp="3cmv8qi14o3uc">
                        <p className="font-bold text-blue-400 text-sm" data-id="element-6414" data-mp="3ckhcw6yminlq">
                          Conductor
                        </p>
                        <p className="text-xs text-blue-400/70" data-id="element-6415" data-mp="33ldu743dca7r">
                          ID: {selectedUser.driver_id}
                        </p>
                      </div>
                    </div>}

                  {selectedUser.has_restaurant_access && <div className="bg-purple-500/5 border border-purple-500/10 rounded-xl p-3 flex items-center gap-3" data-id="element-6416" data-mp="1kn7gktq3xfqr">
                      <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center" data-id="element-6417" data-mp="1fonvswn929zz">
                        <Store className="w-4 h-4 text-purple-400" data-id="element-6418" data-mp="1to5lde8ddrae" />
                      </div>
                      <div data-id="element-6419" data-mp="1ookqbewbm32y">
                        <p className="font-bold text-purple-400 text-sm" data-id="element-6420" data-mp="1opc89mwzw2ar">
                          Acceso a Restaurantes
                        </p>
                        <p className="text-xs text-purple-400/70" data-id="element-6421" data-mp="wxwqc66dv3qv">
                          {selectedUser.restaurant_access_count} restaurante(s)
                        </p>
                      </div>
                    </div>}

                  {!selectedUser.is_platform_admin && !selectedUser.is_driver && !selectedUser.has_restaurant_access && <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center" data-id="element-6422" data-mp="dafiubwwfb2v">
                        <p className="text-sm text-white/40" data-id="element-6423" data-mp="qx5uoxz9ey8m">
                          Sin roles asignados
                        </p>
                      </div>}
                </div>
              </div>

              {/* Personal Info */}
              <div className="space-y-3" data-id="element-6424" data-mp="10sponeu9jmuq">
                <h4 className="text-xs font-bold text-white/30 uppercase tracking-widest" data-id="element-6425" data-mp="15lir1w29ej3n">
                  Información Personal
                </h4>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-4" data-id="element-6426" data-mp="19oyr72djf1l3">
                  <div className="flex items-center gap-3" data-id="element-6427" data-mp="1eo1sqcub6pob">
                    <Phone className="w-4 h-4 text-white/30" data-id="element-6428" data-mp="1jm3dd27dux1w" />
                    <div data-id="element-6429" data-mp="1ojv3bqogw93g">
                      <p className="text-xs text-white/40" data-id="element-6430" data-mp="1okejb1uk4neu">Teléfono</p>
                      <p className="text-sm font-medium" data-id="element-6431" data-mp="3lcoj66gjop2x">
                        {selectedUser.phone || 'No registrado'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-6432" data-mp="27gdg48ct4ocs">
                    <IdCard className="w-4 h-4 text-white/30" data-id="element-6433" data-mp="27hyde5qialec" />
                    <div data-id="element-6434" data-mp="22pevwxop7649">
                      <p className="text-xs text-white/40" data-id="element-6435" data-mp="22o3uv5mn0cgu">Cédula</p>
                      <p className="text-sm font-medium" data-id="element-6436" data-mp="1yna6yc972xny">
                        {selectedUser.national_id || 'No registrada'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-6437" data-mp="2qfj2ndx6nlzh">
                    <UserX className="w-4 h-4 text-white/30" data-id="element-6438" data-mp="2qcwtj9un8yf5" />
                    <div data-id="element-6439" data-mp="2me5u2w2ph4ov">
                      <p className="text-xs text-white/40" data-id="element-6440" data-mp="2mbjqf7ixr9bx">Estado del perfil</p>
                      <p className={`text-sm font-bold ${selectedUser.profile_is_active ? 'text-[#00a870]' : 'text-red-400'}`} data-id="element-6441" data-mp="2hcq7v0osks4w">
                        {selectedUser.profile_is_active ? 'Activo' : 'Inactivo'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* System Info */}
              <div className="space-y-3" data-id="element-6442" data-mp="12i9re469tnkj">
                <h4 className="text-xs font-bold text-white/30 uppercase tracking-widest" data-id="element-6443" data-mp="17h38s7oohypz">
                  Información del Sistema
                </h4>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-4" data-id="element-6444" data-mp="tgiqdcmoe4x0">
                  <div className="flex items-center gap-3" data-id="element-6445" data-mp="ye22a5vwi5ls">
                    <Calendar className="w-4 h-4 text-white/30" data-id="element-6446" data-mp="jqqr453msnm7" />
                    <div data-id="element-6447" data-mp="jp5zoocm5ksk">
                      <p className="text-xs text-white/40" data-id="element-6448" data-mp="oi8v720yb4be">Fecha de registro</p>
                      <p className="text-sm font-medium" data-id="element-6449" data-mp="aqanmvmw1ghc">
                        {formatDate(selectedUser.auth_created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-6450" data-mp="s4t4z3evcmi">
                    <Clock className="w-4 h-4 text-white/30" data-id="element-6451" data-mp="swms4dg70a5" />
                    <div data-id="element-6452" data-mp="5rhd9c2we5le">
                      <p className="text-xs text-white/40" data-id="element-6453" data-mp="5qoeaxa6kkdt">Último acceso</p>
                      <p className="text-sm font-medium" data-id="element-6454" data-mp="3ia0mcu0oqiob">
                        {formatDate(selectedUser.last_sign_in_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-6455" data-mp="3s99j1umxqbed">
                    <Shield className="w-4 h-4 text-white/30" data-id="element-6456" data-mp="3n9wo0mlwty0p" />
                    <div className="min-w-0" data-id="element-6457" data-mp="3n8bv11f8un4y">
                      <p className="text-xs text-white/40" data-id="element-6458" data-mp="540i0hjo1qq2">Auth ID</p>
                      <p className="text-xs font-mono text-white/70 truncate" data-id="element-6459" data-mp="5275wmqaaeao">
                        {selectedUser.user_id}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>}
    </div>;
}